import { Fragment } from 'react';
import { Inline, Tooltip, breakpoints, vars, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { analytics, price as priceUtils } from '@eti/utils';
import { Info } from '@phosphor-icons/react';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.

import { isSeatPreferenceSegment } from '../../utils/segmentUtils';
import { resetButtonStyles } from './styles';
import SummaryUnavailableRow from './SummaryUnavailableRow';
import SummaryTravelerRow from './SummaryTravelerRow';
import SummarySegmentRow from './SummarySegmentRow';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const tableStyles = css`
  display: table;
  table-layout: fixed;
  width: 100%;
`;
const tableBodyStyles = css`
  @media (max-width: 47.9375em) {
    display: block;
  }
`;
const totalPriceStyles = css`
  font-size: 1rem;
  font-weight: 600;
`;
const priceWrapperStyles = css`
  padding-inline: 12px;

  @media (min-width: ${breakpoints._768}) {
    justify-content: flex-end;
    padding-inline: 1rem;
  }
`;
const endAlignedStyles = css`
  display: flex;
  margin-inline-start: auto;

  @media (min-width: ${breakpoints._768}) {
    margin-inline-start: 0;
  }
`;
const infoIconButtonStyles = css`
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;

  &:focus-visible {
    border: 2px solid ${vars.colors.focusRing};
    transition: all 0.2s ease;
  }

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
    margin-top: 0;
  }
`;
const seatSummaryStyles = {
  base: css`
    overflow: hidden auto;
  `,
  flexFill: css`
    flex: 1;
  `,
  surfaceBackground: css`
    background: ${vars.colors.surface.primary};
  `
};
const getSelectedSeat = (id, seats) => seats === null || seats === void 0 ? void 0 : seats.find(seat => seat.id === id);
const getTravelers = (travelers, ids) => travelers.filter(traveler => traveler.id !== undefined && ids.includes(traveler.id));
const isDeckAvailable = deck => Boolean(deck && deck.cabins && deck.cabins.length > 0);
const isAvailable = segment => {
  const {
    mainDeck,
    lowerDeck,
    upperDeck
  } = segment;
  const isUpperDeckAvailable = isDeckAvailable(upperDeck);
  const isMainDeckAvailable = isDeckAvailable(mainDeck);
  const isLowerDeckAvailable = isDeckAvailable(lowerDeck);
  return Boolean(isMainDeckAvailable || isLowerDeckAvailable || isUpperDeckAvailable);
};
const SeatSummary = ({
  isRyanairInTrip,
  onRemoveSeat = () => {},
  onToggleSeatSelect = () => {},
  segments = [],
  selectedSeats = {},
  shouldRenderInline,
  totalMarkup,
  totalPrice,
  travelers = []
}) => {
  const {
    t
  } = useTranslation();
  const totalPriceLabel = t('Product.SeatMap.Summary.TotalPrice');
  const markupLabel = t('SeatMap.PriceInformation.Markup');
  const priceLabel = t('SeatMap.PriceInformation.Price');
  const providerCostLabel = t('SeatMap.PriceInformation.ProviderCost');
  const showToolTip = Number.isInteger(totalMarkup);
  analytics.useGADataLayerEffect({
    event: 'seatingVisible',
    seatingSummary: 'yes'
  });
  return /*#__PURE__*/_jsxs(Stack, {
    "data-testid": "seatMap-summaryTable",
    className: cx(seatSummaryStyles.base, {
      [seatSummaryStyles.surfaceBackground]: shouldRenderInline,
      [seatSummaryStyles.flexFill]: !shouldRenderInline
    }),
    children: [/*#__PURE__*/_jsx("table", {
      className: tableStyles,
      children: /*#__PURE__*/_jsx("tbody", {
        className: tableBodyStyles,
        children: segments === null || segments === void 0 ? void 0 : segments.map(segment => {
          var _selectedSeats$segmen2;
          const isSegmentAvailable = isAvailable(segment);
          const isSeatPreferenceAvailable = isSeatPreferenceSegment(segment);
          return /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(SummarySegmentRow, {
              destination: segment.to,
              isAvailable: isSegmentAvailable || isSeatPreferenceAvailable,
              isOutbound: segment.boundIndex === 0,
              origin: segment.from
            }), isSegmentAvailable ? getTravelers(travelers, segment.travelerIds || []).map(({
              firstName,
              ageType,
              id
            }, travelerIndex) => {
              var _selectedSeats$segmen;
              const selectedSeat = getSelectedSeat(id, (_selectedSeats$segmen = selectedSeats[segment.boundSegmentIndex]) !== null && _selectedSeats$segmen !== void 0 ? _selectedSeats$segmen : []);
              return /*#__PURE__*/_jsx(SummaryTravelerRow, {
                ageType: ageType,
                boundIndex: segment.boundIndex,
                carrier: segment.carrier,
                isRyanairInTrip: isRyanairInTrip,
                name: firstName || '',
                onRemoveSeat: () => onRemoveSeat(segment.boundSegmentIndex, id),
                onToggleSeatSelect: () => onToggleSeatSelect(segment.boundSegmentIndex, id, selectedSeat === null || selectedSeat === void 0 ? void 0 : selectedSeat.deckType),
                segmentIndex: segment.segmentIndex,
                selectedSeat: selectedSeat,
                travelerIndex: travelerIndex
              }, id);
            }) : /*#__PURE__*/_jsx(SummaryUnavailableRow, {
              boundIndex: segment.boundIndex,
              isSeatPreference: isSeatPreferenceAvailable,
              onRemoveOption: () => onRemoveSeat(segment.boundSegmentIndex),
              onToggleOptionSelect: () => onToggleSeatSelect(segment.boundSegmentIndex, 0),
              segmentIndex: segment.segmentIndex,
              selected: (_selectedSeats$segmen2 = selectedSeats[segment.boundSegmentIndex]) !== null && _selectedSeats$segmen2 !== void 0 ? _selectedSeats$segmen2 : []
            }, `${segment.boundIndex}-${segment.segmentIndex}-unavailable`)]
          }, segment.boundSegmentIndex);
        })
      })
    }), /*#__PURE__*/_jsxs(Inline, {
      className: priceWrapperStyles,
      "data-testid": "seatMap-priceWrapper",
      spacing: 8,
      children: [/*#__PURE__*/_jsx("span", {
        children: totalPriceLabel
      }), /*#__PURE__*/_jsxs(Inline, {
        alignY: "center",
        className: endAlignedStyles,
        spacing: 4,
        children: [showToolTip && !isRyanairInTrip && /*#__PURE__*/_jsx(Tooltip, {
          content: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs("div", {
              "data-testid": "seatMap-toolTip-markupLabel",
              children: [markupLabel, ": ", priceUtils.formatPrice(totalMarkup)]
            }), /*#__PURE__*/_jsxs("div", {
              "data-testid": "seatMap-toolTip-providerCostLabel",
              children: [providerCostLabel, ": ", priceUtils.formatPrice(totalPrice - Number(totalMarkup))]
            }), /*#__PURE__*/_jsxs("div", {
              "data-testid": "seatMap-toolTip-totalPriceLabel",
              children: [totalPriceLabel, ": ", priceUtils.formatPrice(totalPrice)]
            })]
          }),
          "data-testid": "seatMap-summary-price-tooltip",
          title: priceLabel,
          children: /*#__PURE__*/_jsx("button", {
            "aria-label": priceLabel,
            className: cx(resetButtonStyles, infoIconButtonStyles),
            "data-testid": "seatMap-informationIcon-button",
            type: "button",
            children: /*#__PURE__*/_jsx(Info, {
              color: vars.colors.signal.information.foreground,
              size: 20,
              weight: "fill"
            })
          })
        }), /*#__PURE__*/_jsx("span", {
          className: totalPriceStyles,
          "data-testid": "seatMap-totalPrice",
          children: priceUtils.formatPrice(totalPrice)
        })]
      })]
    })]
  });
};
export default SeatSummary;
//# sourceMappingURL=SeatSummary.js.map